import { useRef } from 'react'
import { useFrame, useThree } from '@react-three/fiber'
import { useFightStore } from './store'

export type RuntimeConvergenceReport={
  green:boolean
  engineHealth:boolean
  metamotor:boolean
  fighterCount:number
  fighterIntegrity:boolean
  structuralAuthority:string|null
  structuralAuthorityCount:number
  stateIntegrity:boolean
  sceneIntegrity:boolean
  rigIntegrity:boolean
  constructionLogic:boolean
  scenarioArchitecture:boolean
  heroCombatBridge:boolean
  performanceGovernor:boolean
  ecosystemCapabilities:boolean
  ecosystemConnectedCount:number
  ecosystemRequiredCount:number
  faultStreak:number
  enforced:boolean
  reasons:string[]
  observedAt:number
}

const BOOTSTRAP_GRACE_SECONDS=8

export function RuntimeConvergenceAuthority(){
  const {scene}=useThree(),timer=useRef(0),age=useRef(0),samples=useRef(0),faultStreak=useRef(0),everGreen=useRef(false),enforced=useRef(false),pausedByAuthority=useRef(false)
  useFrame((_,dt)=>{
    const safeDt=Math.max(0,dt)
    age.current+=safeDt
    timer.current+=safeDt
    if(timer.current<.35)return
    timer.current=0;samples.current++
    const fighters:Array<{green?:boolean}>=[],authorities:string[]=[]
    scene.traverse(node=>{
      if(node.userData.inyronRuntimeIntegrity)fighters.push(node.userData.inyronRuntimeIntegrity as {green?:boolean})
      if(node.name.startsWith('arena-structural-authority:'))authorities.push(node.name.split(':').at(-1)??'unknown')
    })
    const engineHealth=Boolean(scene.userData.inyronEngineHealth?.engineCount>=10)
    const metamotor=Boolean(scene.userData.inyronMetamotorHealth?.prometheus?.accepted)
    const fighterIntegrity=fighters.length>=2&&fighters.every(f=>f.green===true)
    const structuralAuthorityCount=authorities.length
    const structuralAuthority=structuralAuthorityCount===1?authorities[0]:null
    const stateIntegrity=Boolean(scene.userData.inyronStateSentinel?.ok)
    const sceneIntegrity=Boolean(scene.userData.inyronSceneSentinel?.ok)
    const rigIntegrity=Boolean(scene.userData.inyronRigSentinel?.ok)&&Number(scene.userData.inyronRigSentinel?.suspectFiniteScales??0)===0
    const constructionLogic=Boolean(scene.userData.inyronConstructionLogic)
    const scenarioArchitecture=Boolean(scene.userData.inyronScenarioArchitectureBound)
    const heroCombatBridge=Boolean((window as typeof window&{__INYRON_HERO_COMBAT_BRIDGE__?:{active?:boolean}}).__INYRON_HERO_COMBAT_BRIDGE__?.active)
    const performanceGovernor=Boolean(scene.userData.inyronPerformance)
    const ecosystemReport=scene.userData.inyronEcosystemCapabilityBridge as {green?:boolean;connectedCount?:number;requiredCount?:number}|undefined
    const ecosystemCapabilities=Boolean(ecosystemReport?.green)
    const ecosystemConnectedCount=Number(ecosystemReport?.connectedCount??0)
    const ecosystemRequiredCount=Number(ecosystemReport?.requiredCount??0)
    const reasons:string[]=[]
    if(!engineHealth)reasons.push('engine-health')
    if(!metamotor)reasons.push('metamotor')
    if(!fighterIntegrity)reasons.push('fighter-integrity')
    if(structuralAuthorityCount!==1)reasons.push('structural-authority')
    if(!stateIntegrity)reasons.push('state-integrity')
    if(!sceneIntegrity)reasons.push('scene-integrity')
    if(!rigIntegrity)reasons.push('rig-integrity')
    if(!constructionLogic)reasons.push('construction-logic')
    if(!scenarioArchitecture)reasons.push('scenario-architecture')
    if(!heroCombatBridge)reasons.push('hero-combat-bridge')
    if(!performanceGovernor)reasons.push('performance-governor')
    if(!ecosystemCapabilities)reasons.push('ecosystem-capabilities')
    const green=reasons.length===0
    if(green)everGreen.current=true
    const bootstrapPending=!everGreen.current&&age.current<BOOTSTRAP_GRACE_SECONDS
    faultStreak.current=green||bootstrapPending?0:faultStreak.current+1
    const coreMounted=fighters.length>=2&&structuralAuthorityCount>0&&Boolean(scene.userData.inyronConstructionLogic)
    const mature=samples.current>=10&&coreMounted&&!bootstrapPending
    if(mature&&faultStreak.current>=3&&!enforced.current){
      enforced.current=true
      const state=useFightStore.getState()
      if(state.status==='fight'&&!state.paused){state.setPaused(true);pausedByAuthority.current=true}
      scene.userData.inyronRuntimeContainment={reason:'convergence-fail-closed',reasons:[...reasons],at:performance.now()}
      window.dispatchEvent(new CustomEvent('inyron:runtime-containment',{detail:{reasons:[...reasons],at:performance.now()}}))
    }
    if(green){
      if(pausedByAuthority.current){
        const state=useFightStore.getState()
        if(state.status==='fight'&&state.paused)state.setPaused(false)
        pausedByAuthority.current=false
        scene.userData.inyronRuntimeContainment={reason:'convergence-recovered',reasons:[],at:performance.now()}
        window.dispatchEvent(new CustomEvent('inyron:runtime-recovered',{detail:{at:performance.now()}}))
      }
      enforced.current=false
    }
    const report:RuntimeConvergenceReport={
      green,engineHealth,metamotor,fighterCount:fighters.length,fighterIntegrity,
      structuralAuthority,structuralAuthorityCount,stateIntegrity,sceneIntegrity,rigIntegrity,constructionLogic,scenarioArchitecture,heroCombatBridge,performanceGovernor,
      ecosystemCapabilities,ecosystemConnectedCount,ecosystemRequiredCount,
      faultStreak:faultStreak.current,enforced:enforced.current,reasons,observedAt:performance.now(),
    }
    scene.userData.inyronRuntimeConvergence=report
    ;(window as typeof window&{__INYRON_RUNTIME_CONVERGENCE__?:RuntimeConvergenceReport}).__INYRON_RUNTIME_CONVERGENCE__=report
  })
  return null
}
